import { ExplanationDownloadDto, MediaDownloadDto, MediaUploadDto } from 'collaboration-service';
import { MediaType } from 'components/MediaSelection/FileSelector';
import MediaSelection from 'components/MediaSelection/MediaSelection';
import { Button, Input } from 'imaginarity-react-ui';
import _ from 'lodash';
import * as React from 'react';
import { getTranslated } from 'services/Helpers/TranslationHelpers';
import { useImgI18N } from 'services/ImgI18N';
import { BaseEditItem } from '../GeneralPostEdit';
import { useGPEState } from '../Helpers/UseGPEState';
import { PostEditElementProps } from '../TypeImplementations/PostEdit';

interface EditExplanationProps<T, S> extends PostEditElementProps<T, S> {
}

const EditExplanation = <T extends BaseEditItem = BaseEditItem, S extends ExplanationDownloadDto | undefined = ExplanationDownloadDto | undefined>(p: EditExplanationProps<T, S>) => {
    const { value, setValue, addFileTo } = useGPEState(p.getter);

    const { currentLanguage } = useImgI18N("admin");
    const [explanation, setExplanation] = React.useState<S>();
    const [needSave, setNeedSave] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (value) {
            setExplanation(value);
        }
    }, [value]);

    const onMediaSelected = React.useMemo(() => (file: File, media: MediaUploadDto) => {
        const copy = _.cloneDeep(explanation);
        if (copy) {
            (media as any).file = file;
            copy.media = media as MediaDownloadDto;
            addFileTo(['media']);
            setValue(copy);
        }
    }, [explanation, setValue, addFileTo]);


    const onChangeHeadline = React.useMemo(() => (v?: string) => {
        const copy = _.cloneDeep(explanation) as S;
        if (copy && v) {
            const idx = _.findIndex(copy.headlines, hh => hh.lng === currentLanguage);
            if (idx >= 0)
                copy.headlines[idx].text = v;
            else
                copy.headlines.push({ lng: currentLanguage, text: v });

            setNeedSave(true);
            setExplanation(copy);
        }
    }, [explanation, currentLanguage]);

    const onChangeDescription = React.useMemo(() => (v?: string) => {
        const copy = _.cloneDeep(explanation) as S;
        if (copy && v && copy.descriptions) {
            const idx = _.findIndex(copy.descriptions, hh => hh.lng === currentLanguage);
            if (idx >= 0)
                copy.descriptions[idx].text = v;
            else
                copy.descriptions.push({ lng: currentLanguage, text: v });

            setNeedSave(true);
            setExplanation(copy);
        }
    }, [explanation, currentLanguage]);
    const save = React.useMemo(() => () => {
        const copy = _.cloneDeep(explanation) as S;
        if (copy) {
            setNeedSave(false);
            setValue(copy);
        }
    }, [explanation, setValue]);

    const add = React.useMemo(() => () => {
        const v: ExplanationDownloadDto = {
            headlines: [{ lng: currentLanguage, text: 'generated headline' }],
            media: {} as MediaDownloadDto,
        };
        setValue(v as S);
    }, [setValue, currentLanguage]);

    const onClickDelete = React.useMemo(() => () => {
        setValue(undefined as S);
        setExplanation(undefined);
    }, [setValue]);

    return (
        <div>
            {explanation === undefined &&
                <Button
                    kind='primary'
                    icon='add'
                    onClick={add}
                />
            }
            {explanation &&
                <div>
                    <Button
                        kind='secondary'
                        icon='delete'
                        onClick={onClickDelete}
                    />
                    {needSave &&
                        <Button
                            kind='primary'
                            icon='save'
                            onClick={save}
                        />
                    }
                    <Input
                        value={getTranslated(explanation?.headlines, currentLanguage).text}
                        change={onChangeHeadline}
                        width="100%"
                    />
                    <Input
                        value={getTranslated(explanation?.descriptions, currentLanguage).text}
                        change={onChangeDescription}
                        width="100%"
                    />
                    <MediaSelection
                        width='50%'
                        media={explanation?.media}
                        onMediaSelected={onMediaSelected}
                        uploadType={MediaType.IMAGE} />
                </div>
            }
        </div>
    );

}

export default EditExplanation;
